<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pb-0">
        <div class="tw-flex tw-justify-between tw-items-center">
          <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin tw-my-auto">My Activities</h1>
          <base-button v-show="!isLocationFiltered" @click="add" class="tw-hidden sm:tw-flex">
            <template v-slot:icon>
              <plus-icon />
            </template>
            New Activity
          </base-button>
          <base-icon @click="add" class="tw-inline sm:tw-hidden">
            <plus-icon class="tw-h-5 tw-w-5" />
          </base-icon>
        </div>
        <base-tabs class="tw-mt-2 tw-pb-4 md:tw-pb-0 tw-mb-0" no-border @click="tabClick($event)">
          <base-tab v-for="tab in tabs" :key="tab.title" :selected="$route.meta.component === tab.component" :title="tab.title" />
        </base-tabs>
      </base-content>
    </div>
    <base-content>
      <base-text-field v-model="query" class="w-full" placeholder="Search for a patient" size="sm">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>

      <div id="filter" class="tw-flex tw-justify-end">
        <base-button @click="$refs.filterSlideOver.show()" class="tw-mt-3 tw-mb-2 tw-pr-0" color="primary" size="sm" type="flat">
          <template v-slot:icon>
            <filter-icon />
          </template>
          Filters<base-badge class="tw-ml-2" color="primary" size="sm">{{ countFilters }}</base-badge>
        </base-button>
        <export-button
          class="tw-mt-3 tw-mb-2 tw-pr-0"
          size="sm"
          type="primary"
          label="Export"
          :filename="`PredictileActivities.csv`"
          :requestPayload="requestPayload"
          :url="`/businesses/${businessId}/todo/export?query=${encodeURIComponent(this.query || '')}`"
          @onError="didErrorExport"
        >
        </export-button>
      </div>
      <div class="tw-overflow-auto tw-rounded-lg">
        <component
          :is="currentComponent"
          :business-id="businessId"
          :query="query"
          :filters="filters"
          @deny="deny"
          @approve="approve"
          @assign="assign"
          @assign-location="assignLocation"
          @comment="comment"
          @del="del"
          @history="history"
        />
      </div>
    </base-content>
    <filter-slideover ref="filterSlideOver" :businessId="businessId" @filter-updated="applyFilter" :isCompletedTab="currentTab === 'CompletedTodoView'"></filter-slideover>
    <NewTodoItem ref="newTodoModal" :businessId="businessId" />
    <assign-todo-item ref="assignModal" :businessId="businessId" />
    <assign-location ref="assignLocationModal" :businessId="businessId" />
    <delete-todo-item ref="deleteModal" :businessId="businessId" />
    <addNote ref="addNoteModal" :businessId="businessId" />
    <view-details ref="historyModal" :businessId="businessId" />
    <deny-rx-auth ref="denyModal" :businessId="businessId" />
    <approve-rx-auth ref="approveModal" :businessId="businessId" />
  </div>
</template>

<script>
import moment from "moment";
import NewTodoItem from "@/views/business/todo/components/NewTodoItem";
import ViewDetails from "@/views/business/todo/components/ViewDetails";
import AddNote from "@/views/business/todo/components/AddNote";
import AssignTodoItem from "@/views/business/todo/components/AssignTodoItem";
import AssignLocation from "@/views/business/todo/components/AssignLocation";
import DeleteTodoItem from "@/views/business/todo/components/DeleteTodoItem";
import { PlusIcon, SearchIcon, FilterIcon, SelectAllIcon } from "@/assets/svgs";
import FilterSlideover from "@/views/business/todo/components/FilterSlideover";
import ApproveRxAuth from "@/views/business/todo/components/ApproveRxAuth";
import DenyRxAuth from "@/views/business/todo/components/DenyRxAuth";
import { mapGetters } from "vuex";
import ExportButton from "@/components/ExportButton.vue";

export default {
  name: "BusinessTodoView",
  props: {
    businessId: String,
  },
  components: {
    FilterSlideover,
    NewTodoItem,
    AssignTodoItem,
    DeleteTodoItem,
    PlusIcon,
    SearchIcon,
    FilterIcon,
    SelectAllIcon,
    AddNote,
    ViewDetails,
    ApproveRxAuth,
    DenyRxAuth,
    AssignLocation,
    ExportButton,
    UnassignedTodoView: () => import("@/views/business/todo/business-todo/UnassignedTodoView"),
    AssignedTodoView: () => import("@/views/business/todo/business-todo/AssignedTodoView"),
    CompletedTodoView: () => import("@/views/business/todo/business-todo/CompletedTodoView"),
    OrderInProgressTodoView: () => import("@/views/business/todo/business-todo/OrderInProgressTodoView"),
  },
  data() {
    return {
      query: null,
      itemTypes: [],
      filters: {
        locationsFilter: [],
        typesFilter: [],
        assignedToFilter: [],
        startDate: null,
        endDate: null,
        completedStartDate: null,
        completedEndDate: null,
        completedByFilter: [],
      },
    };
  },
  computed: {
    ...mapGetters("role", { isLocationFiltered: "isLocationFiltered" }),
    currentComponent() {
      return this.$route.meta.component;
    },
    currentTab() {
      return this.$route.meta.component;
    },
    tabs() {
      let tabItems = [];

      if (this.isLocationFiltered === false) tabItems.push({ title: "Unassigned", component: "UnassignedTodoView", route: "unassigned" });

      tabItems.push(
        { title: "Assigned", component: "AssignedTodoView", route: "assigned" },
        { title: "Completed", component: "CompletedTodoView", route: "completed" },
        { title: "Order In-Progress", component: "OrderInProgressTodoView", route: "order-in-progress" },
      );

      return tabItems;
    },
    countFilters() {
      var count = 0;

      if (this.filters.locationsFilter) {
        count += this.filters.locationsFilter.length;
      }

      if (this.filters.typesFilter) {
        count += this.filters.typesFilter.length;
      }

      if (this.filters.assignedToFilter) {
        count += this.filters.assignedToFilter.length;
      }

      if (this.filters.startDate !== null || this.filters.endDate !== null) {
        count++;
      }

      if (
        (this.filters.completedStartDate !== undefined && this.filters.completedStartDate !== null) ||
        (this.filters.completedEndDate !== undefined && this.filters.completedEndDate !== null)
      ) {
        count++;
      }
      if (this.filters.completedByFilter) {
        count += this.filters.completedByFilter.length;
      }
      return count;
    },
    requestPayload() {
      var filterCompletedStartDate = this.filters.completedStartDate ? moment(this.filters.completedStartDate).format("YYYY-MM-DD") : null;
      var filterCompletedEndDate = this.filters.completedEndDate ? moment(this.filters.completedEndDate).format("YYYY-MM-DD") : null;
      var filterStartDate = this.filters.startDate ? moment(this.filters.startDate).format("YYYY-MM-DD") : null;
      var filterEndDate = this.filters.endDate ? moment(this.filters.endDate).format("YYYY-MM-DD") : null;
      let locationFilterStrings = this.filters.locationsFilter
        ? this.filters.locationsFilter.map(function(item) {
            return item;
          })
        : [];
      let typeFilterStrings = this.filters.typesFilter
        ? this.filters.typesFilter.map(function(item) {
            return item;
          })
        : [];
      let assignedToFilterStrings = this.filters.assignedToFilter
        ? this.filters.assignedToFilter.map(function(item) {
            return item;
          })
        : [];
      let completedByFilterStrings = this.filters.completedByFilter
        ? this.filters.completedByFilter.map(function(item) {
            return item;
          })
        : [];
      // Get the current tab value from the selected tab
      const currentTab = this.tabs.find((tab) => tab.component === this.$route.meta.component);

      return {
        startDate: filterStartDate,
        endDate: filterEndDate,
        LocationsFilter: locationFilterStrings,
        TypesFilter: typeFilterStrings,
        AssignedToFilter: assignedToFilterStrings,
        CompletedStartDate: filterCompletedStartDate,
        CompletedEndDate: filterCompletedEndDate,
        CompletedByFilter: completedByFilterStrings,
        tab: currentTab ? currentTab.route : "unassigned",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    },
  },
  watch: {
    currentTab(newTab, oldTab) {
      if (oldTab === "CompletedTodoView" && newTab !== "CompletedTodoView") {
        this.filters.completedStartDate = null;
        this.filters.completedEndDate = null;
        this.$refs.filterSlideOver.completedStartDate = null;
        this.$refs.filterSlideOver.completedEndDate = null;
        this.filters.completedByFilter = [];
      }
    },
  },
  methods: {
    applyFilter(filters) {
      this.filters = filters;
    },
    assign(item) {
      this.$refs.assignModal.show(item);
    },
    assignLocation(item) {
      this.$refs.assignLocationModal.show(item);
    },
    complete(item) {
      this.$refs.completeModal.show(item);
    },
    approve(item) {
      this.$refs.approveModal.show(item);
    },
    deny(item) {
      this.$refs.denyModal.show(item);
    },
    comment(item) {
      this.$refs.addNoteModal.show(item.todoItemId, item.constant);
    },
    history(item) {
      this.$refs.historyModal.show(item);
    },
    del(item) {
      this.$refs.deleteModal.show(item);
    },
    tabClick(tabTitle) {
      const tab = this.tabs.find((t) => t.title === tabTitle);
      this.href(`/businesses/${this.businessId}/business-todo/${tab.route}`);
    },
    href(path) {
      if (this.$route.path !== path) {
        this.$router.push({
          path,
          params: { businessId: this.businessId },
        });
      }
    },
    add() {
      this.$refs.newTodoModal.show();
    },
    handleUpdate() {
      this.EventBus.$emit("activitiesUpdated", {});
    },
    selectAll() {
      this.EventBus.$emit("selectAllTodo", {});
    },
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Failed to download CSV",
        message: "We had a problem downloading the report. Please reload the page to try again.",
        error: err,
      });
    },
  },
};
</script>
